import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../slices/authSlice.mjs';
import { setActiveCategory, setMobileMenuOpen } from '../../slices/headerSlice.mjs';
import { selectNavigationWithForYou } from '../../selectors/navigationSelectors.mjs';
import useIsMounted from '../../hooks/useIsMounted.mjs';
import MobileAppearanceSection from '../common/MobileAppearanceSection.mjs';

/**
 * MobileNavAccordion - Expandable category item for mobile navigation
 * Shows category name with expand arrow, reveals child topics when tapped
 */
function MobileNavAccordion({ item, isClient, onClose }) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const dispatch = useDispatch();

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    // Clicking the text navigates; clicking anywhere else on the row expands
    const handleLinkClick = (e) => {
        e.stopPropagation();
        dispatch(setActiveCategory(item.name));
        onClose();
    };

    return (
        <div className="mobile-nav-accordion">
            {/* Entire header row is clickable for expand/collapse */}
            <button
                type="button"
                className="mobile-nav-accordion-header"
                onClick={toggleExpand}
                aria-expanded={isExpanded}
                aria-label={`Expand ${item.label} topics`}
            >
                {isClient ? (
                    <NavLink
                        to={item.link}
                        className="mobile-nav-accordion-link"
                        onClick={handleLinkClick}
                    >
                        {item.label}
                    </NavLink>
                ) : (
                    <a href={item.link} className="mobile-nav-accordion-link" onClick={handleLinkClick}>
                        {item.label}
                    </a>
                )}
                <svg
                    className="w-5 h-5 transition-transform duration-300"
                    style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
            </button>

            {/* Child topics - collapsible */}
            <div className={`mobile-nav-accordion-content ${isExpanded ? 'expanded' : ''}`}>
                {item.children.map((child) => (
                    <div key={child.id} className="mobile-nav-child">
                        {isClient ? (
                            <Link
                                to={child.link}
                                className="mobile-nav-child-link"
                                onClick={() => {
                                    dispatch(setActiveCategory(item.name));
                                    onClose();
                                }}
                            >
                                {child.label}
                            </Link>
                        ) : (
                            <a href={child.link} className="mobile-nav-child-link">
                                {child.label}
                            </a>
                        )}

                        {/* Sub-children (Level 2) */}
                        {child.children && child.children.length > 0 && (
                            <div className="mobile-nav-subchildren">
                                {child.children.map((subChild) => (
                                    isClient ? (
                                        <Link
                                            key={subChild.id}
                                            to={subChild.link}
                                            className="mobile-nav-subchild-link"
                                            onClick={() => {
                                                dispatch(setActiveCategory(item.name));
                                                onClose();
                                            }}
                                        >
                                            {subChild.label}
                                        </Link>
                                    ) : (
                                        <a
                                            key={subChild.id}
                                            href={subChild.link}
                                            className="mobile-nav-subchild-link"
                                        >
                                            {subChild.label}
                                        </a>
                                    )
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

/**
 * MobileNavigation - Modern mobile navigation drawer
 * Features:
 * - Frosted glass backdrop with blur effect
 * - Spring-like slide animation
 * - Gestural close on backdrop tap
 * - Integrated appearance settings
 */
export default function MobileNavigation() {
    const isClient = useIsMounted();
    const dispatch = useDispatch();
    const panelRef = useRef(null);

    const mobileMenuOpen = useSelector((state) => state.headerSlice.mobileMenuOpen);
    const navigationItems = useSelector(selectNavigationWithForYou);
    const auth = useSelector((state) => state.auth || {});
    const isAuthenticated = auth.isAuthenticated || false;
    const user = auth.user || null;
    const activeCategory = useSelector((state) => state.headerSlice.activeCategory);

    // Close menu handler
    const closeMenu = () => {
        dispatch(setMobileMenuOpen(false));
    };

    // Handle logout
    const handleLogout = async () => {
        await dispatch(logout());
        closeMenu();
    };

    // Handle backdrop click (close menu)
    const handleBackdropClick = (e) => {
        // Only close if clicking the backdrop, not the panel
        if (e.target === e.currentTarget) {
            closeMenu();
        }
    };

    // Body scroll lock
    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.classList.add('overflow-hidden');
        } else {
            document.body.classList.remove('overflow-hidden');
        }
        return () => {
            document.body.classList.remove('overflow-hidden');
        };
    }, [mobileMenuOpen]);

    // Navigation link renderer
    const navLink = (item, onClick) => {
        const isActive = activeCategory === item.name;
        const linkClasses = `mobile-nav-link ${isActive ? 'active' : ''}`;

        return isClient ? (
            <NavLink
                key={item.id}
                to={item.link}
                className={linkClasses}
                onClick={() => {
                    dispatch(setActiveCategory(item.name));
                    onClick?.();
                }}
            >
                {item.label}
            </NavLink>
        ) : (
            <a key={item.id} href={item.link} className={linkClasses}>
                {item.label}
            </a>
        );
    };

    // Portal to document.body to escape the header's stacking context
    // (sticky + z-40 creates a stacking context that traps z-[60] children).
    // Gate on isClient (useIsMounted) so SSR and first hydration render match (both null).
    if (!isClient) return null;

    return createPortal(
        <div
            className={`mobile-nav-overlay ${mobileMenuOpen ? 'open' : ''}`}
            onClick={handleBackdropClick}
            aria-hidden={!mobileMenuOpen}
        >
            <div
                ref={panelRef}
                className={`mobile-nav-panel ${mobileMenuOpen ? 'open' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label="Mobile navigation"
            >
                {/* Header with close button */}
                <div className="mobile-nav-header">
                    <button
                        className="mobile-nav-close"
                        aria-label="Close menu"
                        onClick={closeMenu}
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Navigation items */}
                <nav className="mobile-nav-items">
                    {navigationItems.map((item) => {
                        const hasChildren = item.children && item.children.length > 0;

                        if (!hasChildren) {
                            return navLink(item, closeMenu);
                        }

                        return (
                            <MobileNavAccordion
                                key={item.id}
                                item={item}
                                isClient={isClient}
                                onClose={closeMenu}
                            />
                        );
                    })}
                </nav>

                {/* Auth section */}
                <div className="mobile-nav-auth">
                    {isAuthenticated ? (
                        <div className="mobile-nav-user">
                            <div className="mobile-nav-user-info">
                                <span className="mobile-nav-avatar">
                                    {user?.firstName?.[0]}
                                    {user?.lastName?.[0] || 'U'}
                                </span>
                                <div className="mobile-nav-user-details">
                                    <span className="mobile-nav-user-name">
                                        {user?.firstName} {user?.lastName}
                                    </span>
                                    <span className="mobile-nav-user-email">{user?.email}</span>
                                </div>
                            </div>
                            <div className="mobile-nav-user-actions">
                                {isClient ? (
                                    <Link
                                        to="/my-reviews"
                                        className="mobile-nav-action-link"
                                        onClick={closeMenu}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                            <path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                                        </svg>
                                        My Reviews
                                    </Link>
                                ) : (
                                    <a href="/my-reviews" className="mobile-nav-action-link" onClick={closeMenu}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                            <path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                                        </svg>
                                        My Reviews
                                    </a>
                                )}
                                {isClient ? (
                                    <Link
                                        to="/account"
                                        className="mobile-nav-action-link"
                                        onClick={closeMenu}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="3"></circle>
                                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                                        </svg>
                                        Account Settings
                                    </Link>
                                ) : (
                                    <a href="/account" className="mobile-nav-action-link" onClick={closeMenu}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="3"></circle>
                                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                                        </svg>
                                        Account Settings
                                    </a>
                                )}
                                {/* Theme & Layout - between Account Settings and Logout */}
                                {isClient && <MobileAppearanceSection />}
                                <button
                                    className="mobile-nav-logout"
                                    onClick={handleLogout}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                        <polyline points="16 17 21 12 16 7"></polyline>
                                        <line x1="21" y1="12" x2="9" y2="12"></line>
                                    </svg>
                                    Log Out
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="mobile-nav-auth-buttons">
                            {isClient ? (
                                <>
                                    <Link to="/login" className="mobile-nav-login" onClick={closeMenu}>
                                        Log In
                                    </Link>
                                    <Link to="/signup" className="mobile-nav-signup" onClick={closeMenu}>
                                        Sign Up
                                    </Link>
                                </>
                            ) : (
                                <>
                                    <a href="/login" className="mobile-nav-login" onClick={closeMenu}>
                                        Log In
                                    </a>
                                    <a href="/signup" className="mobile-nav-signup" onClick={closeMenu}>
                                        Sign Up
                                    </a>
                                </>
                            )}
                        </div>
                    )}
                </div>

            </div>
        </div>,
        document.body
    );
}
